import React from 'react';
import { Modal } from '@/app/components/ui/Modal';
import { Button } from '@/app/components/ui/Button';
import { WillDiff } from '@/lib/diff-utils';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface AmendmentSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    diffs: WillDiff[];
    isSaving: boolean;
}

export const AmendmentSummaryModal = ({
    isOpen,
    onClose,
    onSave,
    diffs,
    isSaving,
    error
}: AmendmentSummaryModalProps & { error?: string | null }) => {
    const hasChanges = diffs.length > 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Review Amendments"
            maxWidth="max-w-xl"
        >
            <div className="space-y-6">
                {!hasChanges ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                            <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">No Changes Detected</h3>
                        <p className="text-gray-500 max-w-sm">
                            It looks like you haven't made any changes to your Will since the last save.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-800">
                                <p className="font-medium mb-1">You are about to update your paid Will.</p>
                                <p>Please review the summary of changes below. Once saved, these changes will be reflected in your final document.</p>
                            </div>
                        </div>

                        <div className="border rounded-xl divide-y divide-gray-100 bg-white overflow-hidden">
                            {diffs.map((diff, index) => (
                                <div key={index} className="p-4">
                                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                                        {diff.section}
                                    </h4>
                                    <ul className="space-y-1.5 ml-3.5 border-l-2 border-gray-100 pl-4">
                                        {diff.changes.map((change, i) => (
                                            <li key={i} className="text-sm text-gray-600">
                                                {change}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3 text-red-800">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <p className="font-medium">Error Saving Amendments</p>
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                <div className="flex gap-3 pt-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="flex-1"
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onSave}
                        className="flex-1"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : (hasChanges ? 'Confirm & Save' : 'Close')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
